import React, { useEffect, useState } from 'react';
import { ArrowRightIcon, BatteryFullIcon, CheckIcon, EyeOffIcon, ShieldIcon, XIcon, ZapIcon } from 'lucide-react';
const WEBINAR_URL = '/webinar-mc-adv4-pmax?v=list';
const AMEE_AVATAR_URL = 'https://uploadthingy.s3.us-west-1.amazonaws.com/kJ5b6tBbH8hDNt52JG84mT/Scherm%C2%ADafbeelding_2025-08-15_om_15.44.02.png';
type SignalItem = {
  number: string;
  title: string;
  body: string;
  checkLabel: string;
  imageUrl: string;
  imageAlt: string;
  icon: typeof EyeOffIcon;
};
const signals: SignalItem[] = [{
  number: '01',
  title: 'Hij kijkt weg of gaapt als jij streng wordt',
  body: "Geen negeren, geen desinteresse. Wegkijken en gapen zijn kalmerende signalen: jouw hond zegt 'ik voel spanning, ik wil geen conflict'. Het is het eerste teken dat je correctie niet aankomt zoals jij hem bedoelt.",
  checkLabel: 'Wegkijken of gapen bij spanning',
  imageUrl: 'https://cdn.magicpatterns.com/patterns/generated-images/efb9e1c0-7d1a-4a57-a790-2a001caf4829.jpg',
  imageAlt: 'Hond kijkt rustig weg als kalmerend signaal',
  icon: EyeOffIcon
}, {
  number: '02',
  title: "Zijn gedrag komt 'uit het niets'",
  body: "Uitvallen of grommen 'uit het niets' bestaat eigenlijk niet. Een hond waarschuwt eerst - subtiel. Maar een hond die voor die subtiele signalen gecorrigeerd wordt, slaat ze op den duur over. Wat overblijft is de uitbarsting, zonder aankondiging.",
  checkLabel: "Gedrag dat 'uit het niets' komt",
  imageUrl: 'https://cdn.magicpatterns.com/patterns/generated-images/c8cb4c0a-dcfa-46f3-b0fc-cfe846f06ec0.jpg',
  imageAlt: 'Hond toont een subtiel lichaamssignaal tijdens een rustige wandeling',
  icon: ZapIcon
}, {
  number: '03',
  title: 'Hij doet stoer, maar is bang',
  body: "Gedragsdeskundigen noemen het 'een onzekere hond in een stoer lichaam'. Veel honden die blaffen en uitvallen zien er fel uit, maar zijn bang. Wie dat felle gedrag corrigeert, straft dus een bange hond - en maakt de angst groter.",
  checkLabel: 'Stoer doen maar bang zijn',
  imageUrl: 'https://cdn.magicpatterns.com/patterns/generated-images/4d93520f-d482-487d-8568-1032bf53d616.jpg',
  imageAlt: 'Hond met een alerte houding en zachte, onzekere blik',
  icon: ShieldIcon
}, {
  number: '04',
  title: "Hij staat 'altijd aan'",
  body: 'Kan je hond nooit écht ontspannen? Een hond die correcties niet kan plaatsen, weet nooit waar hij aan toe is. Waakzaamheid wordt zijn standaardstand - thuis, op straat en bij bezoek.',
  checkLabel: 'Altijd aan staan',
  imageUrl: 'https://cdn.magicpatterns.com/patterns/generated-images/a53f93cd-45c8-436d-8dbb-d006a7b7664a.jpg',
  imageAlt: 'Hond rust thuis met een aandachtige blik bij het raam',
  icon: BatteryFullIcon
}];
export function FoutOnzekereHondListPage() {
  const [selectedSignals, setSelectedSignals] = useState<boolean[]>([false, false, false, false]);
  const [showStickyCta, setShowStickyCta] = useState(false);
  const [isStickyDismissed, setIsStickyDismissed] = useState(false);
  const selectedCount = selectedSignals.filter(Boolean).length;
  useEffect(() => {
    const updateStickyCta = () => {
      const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollProgress = scrollableHeight > 0 ? window.scrollY / scrollableHeight : 0;
      setShowStickyCta(scrollProgress >= 0.5);
    };
    updateStickyCta();
    window.addEventListener('scroll', updateStickyCta, {
      passive: true
    });
    window.addEventListener('resize', updateStickyCta);
    return () => {
      window.removeEventListener('scroll', updateStickyCta);
      window.removeEventListener('resize', updateStickyCta);
    };
  }, []);
  function toggleSignal(index: number) {
    setSelectedSignals(currentSignals => currentSignals.map((isSelected, signalIndex) => signalIndex === index ? !isSelected : isSelected));
  }
  return <main className="min-h-screen w-full bg-white font-sans text-[#171716]" data-id="element-1795" data-mp="x4zmtzb2u9yk">
      <header className="border-b border-[#eee9e1]" data-id="element-1796" data-mp="3kat0hpld7jlr">
        <div className="mx-auto flex max-w-[720px] items-center px-5 py-4 sm:px-6" data-id="element-1797" data-mp="3payuvdj0rk7j">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/6YKzftRpGkz4jKTtn8FQxB/Logo_Stressless_Dogs_%288%29_%284%29.png" alt="Stressless Dogs" className="h-auto w-full max-w-[112px] object-contain" data-id="element-1798" data-mp="3pd1u8o62apox" />
        </div>
      </header>

      <article className="mx-auto max-w-[720px] px-5 pb-28 pt-9 sm:px-6 sm:pt-12 md:pb-16" data-id="element-1799" data-mp="31n7jqd97td7u">
        <header data-id="element-1800" data-mp="36kz7cv2tud68">
          <p className="text-xs font-extrabold tracking-[0.16em] text-[#3569B4]" data-id="element-1801" data-mp="36id5nc2marvx">HONDENGEDRAG</p>
          <h1 className="mt-3 font-serif text-[29px] font-bold leading-[1.14] tracking-[-0.025em] text-[#171716] sm:text-[36px] md:text-[40px]" data-id="element-1802" data-mp="3ajyd2hwdudyq">
            De #1 fout waardoor jouw hond onzeker blijft - en de 4 signalen dat het bij jouw hond speelt
          </h1>
          <div className="mt-5 flex items-center gap-3 text-[13px] leading-snug text-[#78736c]" data-id="element-1803" data-mp="2mv5o55e6h6dm">
            <img src={AMEE_AVATAR_URL} alt="Amee, gediplomeerd hondengedragsconsultant" className="h-8 w-8 rounded-full object-cover" data-id="element-1804" data-mp="2fbwav12jwnja" />
            <p data-id="element-1805" data-mp="26b7zke4jl1a3">Met inzichten van Amee, gediplomeerd hondengedragsconsultant · Leestijd: 3 minuten</p>
          </div>
        </header>

        <div className="mt-8 space-y-6 text-[17px] leading-[1.7] text-[#302e2b] sm:text-lg" data-id="element-1806" data-mp="21cwra7twaytz">
          <p data-id="element-1807" data-mp="1wfo8c50xaqro">
            Je hond is superlief. Maar hij blijft onzeker - hoeveel je ook oefent, beloont en traint. Volgens gediplomeerd hondengedragsconsultant Amee komt dat bijna altijd door één fout: <strong data-id="element-1808" data-mp="1mothsogn950f">corrigeren en straffen bij onzeker gedrag</strong>. &quot;Het voelt als duidelijkheid geven. Maar een onzekere hond begrijpt de straf niet - hij wordt er juist onzekerder van.&quot;
          </p>
          <p data-id="element-1809" data-mp="1dmtyfpi5poub">
            De fout zit zo diep in hoe wij honden opvoeden, dat bijna niemand hem bij zichzelf herkent. Daarom: de 4 signalen dat het ook tussen jou en jouw hond speelt.
          </p>
        </div>

        <ol className="mt-10 space-y-7 sm:space-y-9" data-id="element-1810" data-mp="z39etiaqfvuf">
          {signals.map((signal, index) => {
          const Icon = signal.icon;
          return <li key={signal.number} className={`relative overflow-hidden rounded-2xl border p-5 sm:p-6 ${index % 2 === 0 ? 'border-[#e5edf9] bg-[#fbfdff]' : 'border-[#eee9e1] bg-white'}`} data-id="element-1811" data-mp="p40n6w3catb8" data-mpi={index}>
                <span aria-hidden="true" className="pointer-events-none absolute right-4 top-0 font-serif text-[86px] font-bold leading-none text-[#3569B4]/10 sm:right-6 sm:text-[108px]" data-id="element-1812" data-mp="1htqsqgqwomvq">
                  {signal.number}
                </span>
                <div className="relative flex flex-col gap-5 md:flex-row md:items-start" data-id="element-1813" data-mp="1cybg1s4mwx0w">
                  <img src={signal.imageUrl} alt={signal.imageAlt} className="h-44 w-full rounded-xl object-cover md:order-2 md:h-36 md:w-[30%] md:shrink-0" data-id="element-1814" data-mp="20o5u2d0owrmq" />
                  <div className="min-w-0 md:order-1 md:flex-1" data-id="element-1815" data-mp="3l8t1t1mg7qag">
                    <div className="flex items-start gap-3" data-id="element-1816" data-mp="3l66t2yntn4qn">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#e8f0fb] text-[#3569B4]" data-id="element-1817" data-mp="3qhz28cgpm6e">
                        <Icon className="h-[18px] w-[18px]" aria-hidden="true" data-id="element-1818" data-mp="3v076dnt9bgag" />
                      </span>
                      <h2 className="pr-8 font-serif text-[24px] font-bold leading-tight text-[#171716] sm:text-[27px]" data-id="element-1819" data-mp="37acup9kobxrs">
                        {signal.title}
                      </h2>
                    </div>
                    <p className="mt-4 text-[17px] leading-[1.7] text-[#3c3935]" data-id="element-1820" data-mp="3c7mm2urmiji9">{signal.body}</p>
                  </div>
                </div>
              </li>;
        })}
        </ol>

        <section className="mt-10 rounded-2xl border border-[#e9ded1] bg-[#FAF7F2] p-5 sm:p-7" aria-labelledby="zelfcheck-heading" data-id="element-1821" data-mp="rhdxvpfc5hn5">
          <h2 id="zelfcheck-heading" className="font-serif text-[26px] font-bold text-[#171716] sm:text-[29px]" data-id="element-1822" data-mp="2l15u0rixyt9b">
            Even eerlijk: welke herken jij?
          </h2>
          <div className="mt-5 space-y-2.5" data-id="element-1823" data-mp="2uyk8illb17yz">
            {signals.map((signal, index) => {
            const isSelected = selectedSignals[index];
            return <label key={signal.checkLabel} className="flex cursor-pointer items-center gap-3 rounded-xl px-2 py-2 text-[16px] text-[#393630] transition-colors duration-200 hover:bg-white/70" data-id="element-1824" data-mp="2zqtw3uyy5vy3" data-mpi={index}>
                  <input type="checkbox" checked={isSelected} onChange={() => toggleSignal(index)} className="sr-only" data-id="element-1825" data-mp="3dptjkzaq44so" />
                  <span aria-hidden="true" className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md border-2 transition duration-200 ${isSelected ? 'scale-100 border-[#3569B4] bg-[#3569B4] text-white' : 'border-[#aab8ca] bg-white text-transparent'}`} data-id="element-1826" data-mp="1jcbsj2b40psw">
                    <CheckIcon className="h-4 w-4" strokeWidth={3} data-id="element-1827" data-mp="m68w9n2cq7w" />
                  </span>
                  <span data-id="element-1828" data-mp="3sppbqg6kb9qs">{signal.checkLabel}</span>
                </label>;
          })}
          </div>
          <p aria-live="polite" className={`overflow-hidden rounded-xl bg-[#e9f1fc] px-4 text-[15px] font-semibold leading-relaxed text-[#284F9B] transition-[max-height,opacity,margin] duration-300 ease-out ${selectedCount >= 2 ? 'mt-5 max-h-32 py-3 opacity-100' : 'mt-0 max-h-0 py-0 opacity-0'}`} data-id="element-1829" data-mp="odlf4dhynrof">
            Herken je 2 of meer signalen? Dan speelt de correctie-cirkel waarschijnlijk ook bij jouw hond - en is het gratis webinar hieronder precies voor jou.
          </p>
        </section>

        <section className="mt-10" aria-labelledby="omkering-heading" data-id="element-1830" data-mp="2v3ryax71hulp">
          <h2 id="omkering-heading" className="font-serif text-[27px] font-bold leading-tight text-[#171716] sm:text-3xl" data-id="element-1831" data-mp="1ycib321fjjt2">
            De omkering: niet strenger, maar duidelijker
          </h2>
          <div className="mt-5 space-y-6 text-[17px] leading-[1.7] text-[#302e2b] sm:text-lg" data-id="element-1832" data-mp="1tbkwt0jjj9wu">
            <p data-id="element-1833" data-mp="2844wtsbe91a2">
              De oplossing is niet nóg strenger worden. Ook niet alles maar toestaan. Maar de omgekeerde richting: de taal van je hond leren - communiceren met de signalen die hij van nature begrijpt. Dat doorbreekt de cirkel. Het lost niet elk gedragsprobleem in één keer op, maar het is de basis waar al het andere op bouwt.
            </p>
            <p data-id="element-1834" data-mp="2qzv0djyz65q2">
              In het gratis online webinar Communiceren legt Amee die taal uit: welke signalen je hond nu al naar je stuurt, waarom corrigeren averechts werkt en hoe je vandaag nog begint.
            </p>
          </div>
        </section>

        <section className="mt-10 rounded-2xl border border-[#e9ded1] bg-[#FAF7F2] p-6 sm:p-7" aria-labelledby="webinar-heading" data-id="element-1835" data-mp="roboawidkhc6">
          <div className="flex items-center gap-3" data-id="element-1836" data-mp="33cgisezog2mr">
            <img src={AMEE_AVATAR_URL} alt="Amee, gediplomeerd hondengedragsconsultant" className="h-11 w-11 rounded-full object-cover" data-id="element-1837" data-mp="33droikconkgi" />
            <div data-id="element-1838" data-mp="2tjrm4q8sctbh">
              <p className="text-xs font-bold tracking-wide text-[#3569B4]" data-id="element-1839" data-mp="2ykfb76dgf032">GRATIS WEBINAR</p>
              <h2 id="webinar-heading" className="font-serif text-xl font-bold text-[#171716]" data-id="element-1840" data-mp="2kkxjai4v36ph">
                Gratis online webinar: Communiceren met je hond
              </h2>
            </div>
          </div>
          <p className="mt-4 text-[15px] text-[#625f5a]" data-id="element-1841" data-mp="2amzuntj3b7wc">
            Direct online te bekijken · 100% gratis · Voor baasjes van onzekere honden
          </p>
          <a href={WEBINAR_URL} className="mt-5 flex min-h-14 w-full items-center justify-center gap-2 rounded-xl bg-[#E8762D] px-5 py-4 text-center text-base font-extrabold text-white shadow-[0_8px_18px_rgba(188,91,35,0.2)] transition hover:bg-[#cf6525] focus:outline-none focus:ring-4 focus:ring-[#f8c8aa]" data-id="element-1842" data-mp="2fm30hg6w9i5d">
            Bekijk het gratis webinar
            <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-1843" data-mp="7cmrqgpnplg6" />
          </a>
        </section>

        <footer className="mt-10 text-center text-xs text-[#8b867f]" data-id="element-1844" data-mp="2fmvsymitz2s">
          Een publicatie van Stressless Dogs ·{' '}
          <a href="#privacy" className="underline underline-offset-2 hover:text-[#625f5a]" data-id="element-1845" data-mp="3tnip2dqdi6ot">
            Privacyverklaring
          </a>
        </footer>
      </article>

      {!isStickyDismissed && <div className={`fixed inset-x-0 bottom-0 z-50 border-t border-[#e7dfd4] bg-white p-3 shadow-[0_-5px_18px_rgba(30,27,24,0.12)] transition-transform duration-300 md:hidden ${showStickyCta ? 'translate-y-0' : 'translate-y-full'}`} aria-hidden={!showStickyCta} data-id="element-1846" data-mp="q6s9b74zof9x">
          <div className="mx-auto flex max-w-[720px] items-center gap-3" data-id="element-1847" data-mp="1dwcwk286dyp0">
            <p className="min-w-0 flex-1 text-xs font-bold leading-snug text-[#3c3935]" data-id="element-1848" data-mp="1920bjnr1wfb5">
              Gratis webinar: leer de taal van je hond
            </p>
            <a href={WEBINAR_URL} tabIndex={showStickyCta ? 0 : -1} className="shrink-0 rounded-lg bg-[#E8762D] px-3 py-2.5 text-xs font-extrabold text-white focus:outline-none focus:ring-4 focus:ring-[#f8c8aa]" data-id="element-1849" data-mp="1ld9fsv0rsgpx">
              Bekijk nu
            </a>
            <button type="button" onClick={() => setIsStickyDismissed(true)} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[#77716a] focus:outline-none focus:ring-4 focus:ring-[#dce8fa]" aria-label="Sluit de webinarbalk" tabIndex={showStickyCta ? 0 : -1} data-id="element-1850" data-mp="20bv38rh0i0nu">
              <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-1851" data-mp="2j1jkxivurkl9" />
            </button>
          </div>
        </div>}
    </main>;
}